/**
 * Face basis — orthonormal coordinate frame anchored to a surface hit.
 *
 * Used by the Surface Box tool to convert world-space pointer hits on an
 * arbitrary face into a stable (u, v) plane for the rectangle drag, plus a
 * normal axis for the height drag. The basis is locked at footprint
 * pointerdown so subsequent camera motion does not re-pivot the new cube.
 *
 * Tangent disambiguation matches Blender's primitive-on-face convention:
 * the in-plane tangent is the world axis (X / Y / Z) least aligned with
 * the face normal, projected into the face plane. Camera orientation has
 * no influence — the same hit always produces the same basis, so a cube
 * placed on a face stays world-stable when you orbit around. Earlier
 * iteration tried "camera-right projected onto the plane" (BoxCutter
 * convention) which produced visibly skewed footprints on curved /
 * obliquely-oriented faces because camera-right itself rotates with the
 * view; the world-axis approach is the consistent fix.
 */
export type Vec3 = [number, number, number];

export interface FaceBasis {
  /** World-space anchor point — the hit on the host face. */
  origin: Vec3;
  /** First in-plane axis (local +X). */
  tangent: Vec3;
  /** Second in-plane axis (local +Y), `cross(normal, tangent)`. */
  bitangent: Vec3;
  /** Outward face normal (local +Z). */
  normal: Vec3;
}

const NORMAL_EPSILON = 1e-6;

const WORLD_AXES: Vec3[] = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

function lengthSq(v: Vec3): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

function normalize(v: Vec3): Vec3 | null {
  const len2 = lengthSq(v);
  if (len2 < NORMAL_EPSILON * NORMAL_EPSILON) return null;
  const inv = 1 / Math.sqrt(len2);
  return [v[0] * inv, v[1] * inv, v[2] * inv];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function projectOntoPlane(v: Vec3, n: Vec3): Vec3 {
  const d = dot(v, n);
  return [v[0] - d * n[0], v[1] - d * n[1], v[2] - d * n[2]];
}

/**
 * Build an orthonormal face basis at the surface hit.
 *
 * Algorithm (deterministic, world-axis-aligned):
 *   1. Normalize the hit normal. Reject if the input normal is degenerate.
 *   2. Pick the world axis with the smallest |axis · normal| — i.e. the
 *      one least aligned with the normal. Ties (axis-aligned normals) break
 *      X → Y → Z, matching Blender's deterministic basis selection.
 *   3. Project that axis onto the face plane to get the tangent.
 *   4. Bitangent = normal × tangent; re-orthogonalize tangent for stability.
 *
 * @param hit world-space hit position + face normal
 * @returns the basis, or `null` if the hit normal is degenerate
 */
export function computeFaceBasis(hit: {
  worldPosition: Vec3;
  normal: Vec3;
}): FaceBasis | null {
  const n = normalize(hit.normal);
  if (!n) return null;

  // Pick the world axis least aligned with the normal — gives the most
  // numerically stable in-plane projection. For axis-aligned normals the
  // remaining two world axes both project perfectly; X-first tie-break
  // keeps the choice deterministic.
  let bestIdx = 0;
  let bestAlign = Math.abs(dot(WORLD_AXES[0], n));
  for (let i = 1; i < WORLD_AXES.length; i += 1) {
    const align = Math.abs(dot(WORLD_AXES[i], n));
    if (align < bestAlign) {
      bestAlign = align;
      bestIdx = i;
    }
  }

  const tangent =
    normalize(projectOntoPlane(WORLD_AXES[bestIdx], n)) ??
    // Fallback: walk the remaining axes if the chosen one degenerates
    // (shouldn't happen given the least-aligned pick, but guards against
    // pathological floating-point inputs).
    (() => {
      for (const axis of WORLD_AXES) {
        const t = normalize(projectOntoPlane(axis, n));
        if (t) return t;
      }
      return null;
    })();
  if (!tangent) return null;

  const bitangent = normalize(cross(n, tangent));
  if (!bitangent) return null;
  // Re-orthogonalize tangent against the freshly-computed bitangent so
  // tiny projection errors don't accumulate when the basis is reused.
  const tangentNorm = normalize(cross(bitangent, n)) ?? tangent;

  return {
    origin: [hit.worldPosition[0], hit.worldPosition[1], hit.worldPosition[2]],
    tangent: tangentNorm,
    bitangent,
    normal: n,
  };
}

/**
 * Build a surface-grid basis whose visual vertical axis follows world Z.
 *
 * For non-horizontal planes, local +Y (`bitangent`) is the projection of
 * world-up into the plane, so grid lines stay upright even when the surface is
 * rotated around Z. If world-up is parallel to the surface normal, no such
 * projection exists; horizontal planes fall back to the deterministic basis.
 */
export function computeWorldUpFaceBasis(hit: {
  worldPosition: Vec3;
  normal: Vec3;
}): FaceBasis | null {
  const n = normalize(hit.normal);
  if (!n) return null;

  const tangent = normalize(cross([0, 0, 1], n));
  if (!tangent)
    return computeFaceBasis({ worldPosition: hit.worldPosition, normal: n });

  const bitangent = normalize(cross(n, tangent));
  if (!bitangent) return null;

  return {
    origin: [hit.worldPosition[0], hit.worldPosition[1], hit.worldPosition[2]],
    tangent,
    bitangent,
    normal: n,
  };
}

/**
 * Extract intrinsic-XYZ Euler angles from a rotation matrix whose columns
 * are `[tangent, bitangent, normal]`.
 *
 * Matches Three.js `Euler.setFromRotationMatrix(m, "XYZ")` for the same
 * matrix layout — confirmed against THREE's reference implementation. We
 * inline the math here to keep this module THREE-free; callers construct
 * THREE objects from the result at the boundary.
 *
 *   m = [ tx  bx  nx ]
 *       [ ty  by  ny ]
 *       [ tz  bz  nz ]
 *
 *   y = asin(clamp(m02, -1, 1))           // m02 = nx
 *   if (|m02| < ~1):
 *     x = atan2(-m12,  m22)               // (-ny,  nz)
 *     z = atan2(-m01,  m00)               // (-bx,  tx)
 *   else (gimbal lock — nx = ±1):
 *     x = atan2( m21,  m11)               // ( bz,  by)
 *     z = 0
 */
export function eulerXYZFromBasis(basis: FaceBasis): Vec3 {
  const m00 = basis.tangent[0];
  const m01 = basis.bitangent[0];
  const m11 = basis.bitangent[1];
  const m21 = basis.bitangent[2];
  const m02 = basis.normal[0];
  const m12 = basis.normal[1];
  const m22 = basis.normal[2];

  const y = Math.asin(Math.max(-1, Math.min(1, m02)));
  let x: number;
  let z: number;
  if (Math.abs(m02) < 0.9999999) {
    x = Math.atan2(-m12, m22);
    z = Math.atan2(-m01, m00);
  } else {
    x = Math.atan2(m21, m11);
    z = 0;
  }
  return [x, y, z];
}
